import React, { useRef, useState } from "react";
import { Container, Row, Col, Button, Form } from "react-bootstrap";

// --- INLINE ERP UTILITIES ---
const generateDocumentRef = ({ prefix = "HR", department = "ENG", empId = "" }) => {
  const deptCode = department ? department.substring(0, 3).toUpperCase() : "GEN";
  const codePart = empId || "0921";
  return `${prefix}-${deptCode}-${codePart}`;
};

const getTenurePeriod = (joiningYear, type) => {
  return `${type || "Full-Time"} (Permanent Contract)`;
};

const getEmployeeSalutation = (gender) => {
  const isFemale = gender?.toLowerCase() === "female" || gender?.toLowerCase() === "f";
  return {
    salute: isFemale ? "Ms." : "Mr.",
    pronoun: isFemale ? "she" : "he",
    possessive: isFemale ? "Her" : "His",
    facility: "Corporate Headquarters",
  };
};

// --- DEMO DATA DEFAULT ---
const defaultEmployee = {
  firstName: "Eleanor",
  middleName: "Marie",
  lastName: "Vance",
  gender: "female",
  emergencyContactName: "David Vance",
  residentialAddress: "Suite 402, Highstreet Avenue, Bangalore, KA",
  designation: "Senior Software Architect",
  department: "Engineering & Innovation",
  joiningYear: "2023",
  employmentType: "Full-Time Regular",
  employeeId: "EMP-4091",
};

export default function EmploymentVerificationLetter({ employee = defaultEmployee }) {
  const printRef = useRef();

  const { salute, possessive, facility } = getEmployeeSalutation(
    employee?.gender
  );

  const [formData, setFormData] = useState({
    refNo: generateDocumentRef({
      prefix: "HR",
      department: employee?.department,
      empId: employee?.employeeId,
    }),

    date: new Date().toLocaleDateString("en-IN"),

    employeeName:
      [employee?.firstName, employee?.middleName, employee?.lastName]
        .filter(Boolean)
        .join(" ") || "",

    parentName: employee?.emergencyContactName || "",
    address: employee?.residentialAddress || "",

    designation: employee?.designation || "",
    department: employee?.department || "",
    joiningYear: employee?.joiningYear || "",

    tenure: getTenurePeriod(
      employee?.joiningYear || "",
      employee?.employmentType
    ),

    employeeId: employee?.employeeId || "",

    governingPolicy: "Corporate Human Resources & Labor Compliance Code",
    complianceStandard: "ISO 27001 & Standard Employment Policy Regulations",

    companyName: "ACME GLOBAL ENTERPRISES",
    addressLine: "Corporate Tower, Tech Park, Bangalore, India",
  });

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePrint = () => {
    const content = printRef.current.innerHTML;
    const win = window.open("", "", "width=900,height=700");

    win.document.write(`
      <html>
        <head>
          <title>Employment Verification Certificate</title>
          <style>
            body {
              font-family: "Segoe UI", Tahoma, Geneva, Verdana, sans-serif;
              padding: 120px 40px;
              line-height: 1.8;
              color: #111;
            }
            .center { text-align: center; }
            .right { text-align: right; }
          </style>
        </head>
        <body>${content}</body>
      </html>
    `);

    win.document.close();
    win.print();
  };

  return (
    <Container className="mt-4">
      {/* FORM CONTROLS */}
      <Form className="mb-4">
        <Row className="mb-2">
          <Col>
            <Form.Control
              name="refNo"
              value={formData.refNo}
              onChange={handleChange}
              placeholder="Ref No."
            />
          </Col>
          <Col>
            <Form.Control
              name="date"
              value={formData.date}
              onChange={handleChange}
              placeholder="Date"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="employeeName"
              value={formData.employeeName}
              onChange={handleChange}
              placeholder="Employee Name"
            />
          </Col>
          <Col>
            <Form.Control
              name="parentName"
              value={formData.parentName}
              onChange={handleChange}
              placeholder="Emergency Contact / Guardian"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="designation"
              value={formData.designation}
              onChange={handleChange}
              placeholder="Designation / Role"
            />
          </Col>
          <Col>
            <Form.Control
              name="joiningYear"
              value={formData.joiningYear}
              onChange={handleChange}
              placeholder="Year / Date of Joining"
            />
          </Col>
        </Row>

        <Row className="mb-2">
          <Col>
            <Form.Control
              name="tenure"
              value={formData.tenure}
              onChange={handleChange}
              placeholder="Employment Tenure / Status"
            />
          </Col>
          <Col>
            <Form.Control
              name="employeeId"
              value={formData.employeeId}
              onChange={handleChange}
              placeholder="Employee ID"
            />
          </Col>
        </Row>
      </Form>

      <div className="text-end mb-3">
        <Button onClick={handlePrint} variant="primary">
          Print Verification Letter
        </Button>
      </div>

      {/* LETTER DOCUMENT AREA */}
      <div ref={printRef} style={{ padding: 25, border: "1px solid #000" }}>
        <div style={{ marginTop: "160px" }}>
          {/* REF + DATE */}
          <Row className="mt-3">
            <Col>
              <strong>Ref No: {formData.refNo}</strong>
            </Col>
            <Col className="right">
              <strong>Date: {formData.date}</strong>
            </Col>
          </Row>

          {/* TITLE */}
          <Row className="mt-4">
            <Col className="center">
              <h4>
                <u>EMPLOYMENT VERIFICATION CERTIFICATE</u>
              </h4>
            </Col>
          </Row>

          {/* BODY */}
          <div className="mt-4">
            <p>
              This is to certify that{" "}
              <strong>
                {salute} {formData.employeeName}
              </strong>
              , emergency contact recorded as Mr./Ms.{" "}
              <strong>{formData.parentName}</strong>, residing at{" "}
              <strong>{formData.address}</strong>, is a bona fide active employee
              of our organization, holding the designation of{" "}
              <strong>{formData.designation}</strong> in the{" "}
              <strong>{formData.department}</strong> department since the fiscal
              year <strong>{formData.joiningYear}</strong>.
            </p>

            <p>
              The employment classification is{" "}
              <strong>{formData.tenure}</strong>. {possessive} Employee Code / ID is{" "}
              <strong>{formData.employeeId}</strong>.
            </p>

            <p>
              {salute} {formData.employeeName} is currently operating out of
              our <strong>{facility}</strong> facility.
            </p>

            <p>
              This certification is issued in compliance with{" "}
              <strong>{formData.governingPolicy}</strong> and aligns with{" "}
              <strong>{formData.complianceStandard}</strong>.
            </p>
          </div>

          {/* FOOTER */}
          <Row className="mt-5" style={{ paddingTop: "40px" }}>
            <Col className="text-end">
              <strong>HUMAN RESOURCES DEPARTMENT</strong>
              <div>Authorized Officer</div>
            </Col>
          </Row>
        </div>
      </div>
    </Container>
  );
}